import { useState } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	FormControl,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	TextField,
	Tooltip,
	useTheme,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import useStyles from "./styles";
import statusVersao from "../statusVersao";
import DatagridComponent from "../../../components/datagridComponent";
import ListaClientesDoItem from "../listaClientesDoItem";
import PainelSaudeColeta from "../painelSaudeColeta";
import EstadoVazio from "../estadoVazio";
import ModalAtualizarTodos from "../modalAtualizarTodosScripts";
import alvoId from "../alvoId";

// Opcoes fixas do filtro de status - mesmos literais que abaTemplates usa. "Sem versao" tambem pode
// ocorrer numa linha de script (versaoCliente vazio/nulo pra uma chave que existe na referencia)
const OPCOES_STATUS = [
	"Atualizado",
	"Desatualizado",
	"À frente do padrão",
	"Só no cliente",
	"Ausente no cliente",
	"Sem versão",
];

export default function AbaScripts({
	scripts,
	saudeColeta,
	loading,
	linhaSelecionada,
	setLinhaSelecionada,
	filtroCliente,
	setFiltroCliente,
	filtroStatus,
	setFiltroStatus,
	onAtualizarCache,
	coletando,
	onScriptAtualizado,
}) {
	const [modalAtualizarTodosAberto, setModalAtualizarTodosAberto] = useState(false);
	const theme = useTheme();
	const styles = useStyles(theme);
	const cores = statusVersao(theme);

	// Limpa os dois campos de filtro de uma vez (acao do estado vazio "filtro sem resultado")
	const limparFiltro = () => {
		setFiltroCliente?.("");
		setFiltroStatus?.("");
	};

	const listaScripts = scripts || [];
	const listaSaude = saudeColeta || [];
	const filtroAtivo = Boolean(filtroCliente) || Boolean(filtroStatus);
	// Soma de TODOS os pares cliente/script pendentes (desatualizado ou sem o script), nao so os filtrados
	// na tela - o botao "Atualizar todos" afeta o sistema inteiro, o filtro de busca/status e so visual
	const totalPendentes = listaScripts.reduce(
		(soma, script) => soma + (script?.desatualizado || 0) + (script?.ausente || 0),
		0,
	);

	// Resumo "X de Y scripts atualizados" por cliente, pro painel de saude - conta so os scripts que
	// existem no catalogo de referencia (existeNaReferencia === true); linhas "So no cliente" nao tem
	// referencia pra comparar, entao nunca poderiam virar "Atualizado" e inflavam o "total" pra centenas.
	// De quantos scripts de referencia o cliente tem instalado, quantos estao "Atualizado" ou "A frente
	// do padrao" - mesmo criterio de "ok" usado no contador do grid principal (ver getComparativoScripts,
	// contadores.ok). Usa a lista completa (nao a filtrada), o resumo nao deve mudar com o filtro da tela.
	// Indexado por alvo (cliente + proxy) - cada proxy regional tem a sua propria contagem
	const resumoPorCliente = new Map();
	const scriptsDeReferencia = listaScripts.filter((script) => script?.existeNaReferencia === true);
	for (const cliente of scriptsDeReferencia.flatMap((script) => script?.clientes || [])) {
		if (!cliente?.clienteId || cliente?.status === "Ausente no cliente") continue;
		const atual = resumoPorCliente.get(alvoId(cliente)) || { atualizados: 0, total: 0 };
		atual.total += 1;
		if (cliente?.status === "Atualizado" || cliente?.status === "À frente do padrão") {
			atual.atualizados += 1;
		}
		resumoPorCliente.set(alvoId(cliente), atual);
	}

	// Filtragem em memoria: texto por nome/chave do script e presenca de algum cliente com o status escolhido
	const scriptsFiltrados = listaScripts.filter((script) => {
		const termo = filtroCliente?.toLowerCase()?.trim();
		const combinaTexto =
			!termo ||
			script?.nome?.toLowerCase()?.includes(termo) ||
			script?.chave?.toLowerCase()?.includes(termo);
		const combinaStatus =
			!filtroStatus ||
			script?.clientes?.some((cliente) => cliente?.status === filtroStatus);
		return combinaTexto && combinaStatus;
	});

	const rows = scriptsFiltrados.map((script) => ({
		...script,
		id: script?.chave,
	}));

	const columns = [
		{ field: "nome", headerName: "Script", flex: 1, minWidth: 160 },
		{
			field: "versaoReferencia",
			headerName: "Versão referência",
			width: 170,
			// Linha "so no cliente": chave sem linha formal na referencia central, mostra chip em vez de celula vazia
			// (existeNaReferencia === false e o unico sinal confiavel, mesmo criterio de abaTemplates)
			renderCell: (params) => {
				if (params?.row?.existeNaReferencia === false) {
					return (
						<Chip
							size="small"
							label="Só no cliente"
							sx={{
								...styles.chip,
								backgroundColor: cores["Só no cliente"]?.background,
								color: cores["Só no cliente"]?.color,
							}}
						/>
					);
				}
				return params?.row?.versaoReferencia;
			},
		},
		{ field: "ok", headerName: "Clientes OK", width: 130 },
		{ field: "desatualizado", headerName: "Desatualizados", width: 150 },
		{ field: "ausente", headerName: "Sem o script", width: 150 },
	];

	// Enquanto carrega, skeleton no formato do grid - nunca spinner solto no meio do conteudo
	if (loading) {
		return (
			<Box sx={styles.skeletonContainer}>
				<Skeleton
					variant="rectangular"
					height={40}
					sx={styles.skeletonHeader}
				/>
				{Array.from({ length: 8 }).map((_, index) => (
					<Skeleton
						key={index}
						variant="rectangular"
						height={32}
						sx={styles.skeletonRow}
					/>
				))}
			</Box>
		);
	}

	// Nenhum cliente com Zabbix cadastrado ainda
	if (listaScripts.length === 0 && listaSaude.length === 0) {
		return <EstadoVazio tipo="sem-clientes" entidade="scripts" />;
	}

	// Clientes cadastrados, mas nenhuma coleta rodou ainda para nenhum deles
	if (
		listaScripts.length === 0 &&
		listaSaude.length > 0 &&
		!listaSaude.some((cliente) => cliente?.status !== null)
	) {
		return (
			<EstadoVazio
				tipo="sem-coleta"
				entidade="scripts"
				onAction={onAtualizarCache}
				disabled={coletando}
			/>
		);
	}

	return (
		<Box sx={styles.container}>
			<Box sx={styles.barraFiltro}>
				<TextField
					size="small"
					label="Buscar"
					placeholder="Nome ou chave do script"
					value={filtroCliente || ""}
					onChange={(event) => setFiltroCliente?.(event.target.value)}
					sx={styles.buscaField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
						endAdornment: filtroCliente ? (
							<InputAdornment position="end">
								<Clear
									fontSize="small"
									sx={styles.limparBusca}
									onClick={() => setFiltroCliente?.("")}
								/>
							</InputAdornment>
						) : null,
					}}
				/>
				<FormControl size="small" sx={styles.statusField}>
					<Select
						value={filtroStatus || ""}
						displayEmpty
						onChange={(event) => setFiltroStatus?.(event.target.value)}
					>
						<MenuItem value="">Todos os status</MenuItem>
						{OPCOES_STATUS.map((status) => (
							<MenuItem key={status} value={status}>
								{status}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Tooltip
					title={
						totalPendentes === 0
							? "Nenhum script desatualizado ou ausente no momento"
							: ""
					}
					disableHoverListener={totalPendentes > 0}
				>
					<span>
						<Button
							variant="contained"
							sx={styles.botaoAtualizarTodos}
							disabled={totalPendentes === 0}
							onClick={() => setModalAtualizarTodosAberto(true)}
						>
							Atualizar todos
						</Button>
					</span>
				</Tooltip>
			</Box>
			{scriptsFiltrados.length === 0 && filtroAtivo ? (
				<EstadoVazio tipo="filtro-sem-resultado" onAction={limparFiltro} />
			) : (
				// Paper garante o fundo/contraste corretos do grid nos dois modos, mesmo padrao ja usado em abaTemplates/Clientes/Staff/Home
				<Paper
					sx={styles.paper}
					elevation={theme.palette.mode === "dark" ? 0 : 3}
				>
					<Box sx={styles.gridContainer}>
						<DatagridComponent
							rows={rows}
							columns={columns}
							setSelectedRow={setLinhaSelecionada}
							sxDatagrid={{
								height: "100%",
								"& .MuiDataGrid-row.Mui-selected": {
									backgroundImage: theme.palette.gradient.buttonSelected,
									"& .MuiDataGrid-cell": {
										color: theme.palette.white.main,
									},
								},
								"& .MuiDataGrid-row:hover": {
									backgroundImage: `linear-gradient(
									90deg,
									${alpha(theme.palette.buttonHoverGradient.dark, 0.5)} 0%,
									${alpha(theme.palette.buttonHoverGradient.light, 0.5)} 100%
								)`,
								},
							}}
							options={{
								disableRowSelectionOnClick: false,
							}}
						/>
					</Box>
				</Paper>
			)}
			<ListaClientesDoItem
				item={linhaSelecionada}
				onTemplateAtualizado={onScriptAtualizado}
				tipo="script"
				scripts={listaScripts}
				saudeColeta={listaSaude}
			/>
			<PainelSaudeColeta
				saudeColeta={listaSaude}
				resumoPorCliente={resumoPorCliente}
				scripts={listaScripts}
				onScriptAtualizado={onScriptAtualizado}
			/>
			<ModalAtualizarTodos
				open={modalAtualizarTodosAberto}
				onClose={() => setModalAtualizarTodosAberto(false)}
				scripts={listaScripts}
				onConcluido={onScriptAtualizado}
			/>
		</Box>
	);
}
